// ONE reason→message mapping for the staff app (parity Phase 0.4). Ported
// from the widget's describeReason (apps/widget/src/components/
// ServicePanel.tsx) and replaces the two divergent local copies that lived
// in the reservation + walk-in screens. Those copies missed every rule-
// engine reason (blackout_*, min/max_stay, lead-time) and one mapped the
// WRONG key ("outside_service_hours" vs the server's
// "outside_operating_hours"), so blocked bookings surfaced as raw codes.
//
// Accepts either a plain reason string or the whole HoldFailure payload —
// when the rule engine provides context (rule_name / nights / lead hours),
// staff see exactly which rule blocked the booking, same as guests do.

import { specialDateChangeWords } from "./special-date-start-in";

export type FailureLike = string | { reason: string; [k: string]: unknown };

export function describeReason(failure: FailureLike): string {
  const r = typeof failure === "string" ? { reason: failure } : failure;
  const reason = r.reason;
  switch (reason) {
    case "seat_taken":
      return "That seat is already booked for this time.";
    case "service_capacity_exceeded":
      return "This service is fully booked for that time. Try a different time or smaller party.";
    case "plan_capacity_exceeded":
      return "This area is fully booked for that time. Try a different area or time.";
    case "seating_area_full":
      // Raised by trg_enforce_seating_area_capacity (20260831000030) when a
      // seating area's per-area cap is reached for the requested window.
      return "That seating area is fully booked for the time you picked — choose another area or time.";
    case "rooms_sold_out":
      return "That room type is fully booked for those dates. Try different dates or another room.";
    case "unit_sold_out":
      // The non-accommodation twin of rooms_sold_out, introduced with per-unit
      // resource capacity (20260822000020). Without this case it reached the
      // guest as "Couldn't complete the booking (unit_sold_out)." — and the
      // room wording above is the wrong sentence about a horse or a golf bay.
      return "That option is fully booked for the time you picked. Try another time.";
    case "unit_capacity_short":
      // The chosen table, room or option seats fewer guests than asked: the
      // party-size editor's check, and a back-office menu split on one table
      // (20260925090080). capacity_sum is what the option(s) seat.
      return typeof r.capacity_sum === "number" && r.capacity_sum > 0
        ? `The selected option seats up to ${r.capacity_sum} guest${r.capacity_sum === 1 ? "" : "s"} — choose a bigger one or add another.`
        : "The selected option can't take that many guests — choose a bigger one or add another.";
    case "menu_lines_need_single_hold":
      // 20260925090080: on a date with guest menus a table or item line is
      // ONE hold sized by its split, so a line of several is refused.
      return "On a date with guest menus each table or item is booked at quantity one, with the guests who sit at it — set the option to one.";
    case "off_grid_start": {
      // The spa publishes the only start times it sells
      // (tenant_integrations.config.booking_times). Enforced server-side from
      // 20260831000040 — before that only the widget and the admin screen
      // filtered, so the staff app could sell a time the venue had withdrawn.
      const allowed = typeof r.allowed_times === "string" ? r.allowed_times : "";
      return allowed
        ? `This spa starts treatments at ${allowed}. Pick one of those times.`
        : "This spa only starts treatments at set times — pick one of the times it offers.";
    }
    case "start_time_not_offered": {
      // availability_rules kind start_times (20260919090020): the option runs
      // at fixed times, and allowed_times lists that day's ("09:00 and 14:00").
      const allowed = typeof r.allowed_times === "string" ? r.allowed_times : "";
      return allowed
        ? `On that day this starts at ${allowed} only. Pick one of those times.`
        : "This only starts at set times — pick one of the times offered.";
    }
    // Special dates with guest menus (20260925090010). The server carries
    // the wording for a short or long split in `hint`.
    case "menu_selection_required":
      // Picker-neutral on purpose: the staff reservation screen answers this
      // reason by SHOWING the menus the server named, next to this copy. A
      // surface with no chooser on screen overrides it with its own next
      // step (the widget: reload; mobile: book elsewhere; staff walk-in:
      // take it as a reservation).
      return "Choose a menu for each guest to continue.";
    case "menu_split_mismatch":
      return typeof r.hint === "string" && r.hint
        ? r.hint + "."
        : "The menu choices don't add up to your party — check them and try again.";
    // A special date's party rules. They come back on the menu path (jsonb,
    // with a hint) AND from trg_enforce_special_date_party on a date with no
    // menus at all, so the fallback must not mention menus.
    case "party_not_allowed":
    case "party_out_of_range":
      return typeof r.hint === "string" && r.hint
        ? r.hint + "."
        : "That party size isn't available on this date — choose one of the sizes it offers.";
    case "menu_not_found":
    case "invalid_menu_lines":
      return "This date's menus have changed — reload to see the current menus.";
    case "menu_lines_without_special_date":
      return "That time isn't part of the special date any more — pick a time within it.";
    case "menu_hold_requires_new_hold":
    case "menu_hold_event_changed":
      return "To change a booking with menu choices, remove it and add it again.";
    // The start-in rule (20261004090010): an edit or move never changes a
    // booking's special date. The server words it (hint) with the date's name.
    case "special_date_would_change":
      return specialDateChangeWords(r);
    case "menu_unavailable_on_target":
      return typeof r.hint === "string" && r.hint
        ? `That date doesn't offer the ${r.hint} menu — choose another date.`
        : "That date doesn't offer one of your menus — choose another date.";
    case "service_unavailable":
      return "This service isn't available.";
    case "accommodation_requires_room":
      // Reached the guest as "Couldn't complete the booking
      // (accommodation_requires_room)." before this case existed.
      return "Choose a room type to continue — a stay has to be booked against a specific room.";
    case "unpriced_accommodation_unit":
      return "That room doesn't have rates loaded yet, so it can't be booked online — pick another room or send an enquiry.";
    case "plan_unavailable":
      return "This floor plan isn't available.";
    case "invalid_window":
      return "Pick a booking window that ends after it starts.";
    case "invalid_party_size":
      return "Party size must be at least 1.";
    case "invalid_seating_area":
      return "That seating area isn't available for this service.";
    case "staff_only_area":
      // Raised by trg_enforce_staff_only_seating_area (20260902000010) when a
      // guest-channel write targets an area reserved for the venue team.
      return "That seating area is booked by the venue team only — please choose another area.";

    // ── Large-party policy (party_size_max_auto + oversize_party_policy) ──
    // The hold RPCs return party_size_max_auto with both reasons; name the
    // limit when we have it. The widget adds an Enquire CTA on the enquiry
    // variant — this copy has to stand alone for staff/admin surfaces too.
    case "oversize_party_enquiry":
      return typeof r.party_size_max_auto === "number"
        ? `Groups larger than ${r.party_size_max_auto} book by enquiry — send one and the venue will arrange it with you.`
        : "Groups this size book by enquiry — send one and the venue will arrange it with you.";
    case "oversize_party_blocked":
      return typeof r.party_size_max_auto === "number"
        ? `This experience takes up to ${r.party_size_max_auto} guests per booking — please contact the venue about larger groups.`
        : "That party is larger than this experience takes online — please contact the venue directly.";
    case "outside_operating_hours":
      return "That time is outside the service's operating hours.";

    // ── Fewest guests per booking on a per-person option (20260926090030) ──
    // scope 'option': a hold / cart edit / addition on one option; the server
    // names the option and the service's guest noun. scope 'visit': the
    // visit's guest count cannot drop below the sum of its options' minimums.
    case "party_below_minimum": {
      const n = typeof r.party_size_min === "number" ? r.party_size_min : null;
      const noun = typeof r.noun === "string" && r.noun.trim() ? r.noun.trim() : "guests";
      if (n === null) return "This option needs a bigger group per booking.";
      if (r.scope === "visit") {
        return `This booking can't go below ${n} ${noun}: each option has a minimum group size. Cancel an option instead if fewer are coming.`;
      }
      return typeof r.option_name === "string" && r.option_name.trim()
        ? `${r.option_name.trim()} needs at least ${n} ${noun} per booking.`
        : `This option needs at least ${n} ${noun} per booking.`;
    }

    // ── Booking questions and checkout confirmations (20260926090020) ──
    case "participant_details_required":
      return "Please complete the booking details before paying.";
    case "checkout_confirmations_required":
      return "Please tick each confirmation before paying.";
    case "confirmations_changed":
      return "The booking terms were just updated. Please review and tick them again.";
    // A staff-rank user's save that would need a sensitive answer they cannot
    // see (a rider's medical or guardian details): a manager makes it.
    case "sensitive_answers_need_manager":
      return "This change affects medical or guardian details, which only a manager can see. Ask a manager to make it.";
    // A visit whose service asks each guest questions cannot grow by a bare
    // count: the extra guest is added as an addition that asks their details.
    case "guest_details_needed": {
      const noun = typeof r.noun === "string" && r.noun.trim() ? r.noun.trim() : "guests";
      return `Extra ${noun} have their own booking details to fill in, so they can't be added by changing the number. Add them as a new item instead, or contact the venue.`;
    }

    // ── Tenant-authored closures (service_closures) ────────────────
    // guest_message is the venue's own sentence, written FOR guests in the
    // closure editor — render it verbatim when present. The fallback names
    // the date, never any internal label.
    case "service_closed": {
      const msg =
        typeof r.guest_message === "string" && r.guest_message.trim().length > 0
          ? r.guest_message.trim()
          : null;
      if (msg) return msg;
      const day = r.closed_on ? formatDate(String(r.closed_on)) : null;
      if (r.partial_day && r.closed_from_time && r.closed_to_time) {
        const from = String(r.closed_from_time).slice(0, 5);
        const to = String(r.closed_to_time).slice(0, 5);
        return day
          ? `The venue is closed ${from}–${to} on ${day} — please pick another time.`
          : `The venue is closed ${from}–${to} that day — please pick another time.`;
      }
      return day
        ? `The venue is closed on ${day} — please pick another date.`
        : "The venue is closed on that date — please pick another date.";
    }

    // ── Rule engine violations ─────────────────────────────────────
    case "blackout_dow":
      return r.rule_name
        ? `Not available — ${r.rule_name}${r.blocked_date ? ` (${formatDate(String(r.blocked_date))})` : ""}.`
        : "Not available on the day you picked.";
    case "blackout_range":
      return r.rule_name
        ? `Not available — ${r.rule_name}${r.range_start && r.range_end ? ` (${formatDate(String(r.range_start))} → ${formatDate(String(r.range_end))})` : ""}.`
        : "Not available in that date range.";
    case "blackout_date":
      return r.rule_name
        ? `Not available on ${r.blocked_date ? formatDate(String(r.blocked_date)) : "that date"} (${r.rule_name}).`
        : "Not available on that date.";
    case "min_stay":
      return r.min_nights
        ? `Minimum stay is ${r.min_nights} night${r.min_nights === 1 ? "" : "s"}${
            r.actual_nights ? ` (you picked ${r.actual_nights})` : ""
          }.`
        : "Stay too short for the rules on this service.";
    case "max_stay":
      return r.max_nights
        ? `Maximum stay is ${r.max_nights} night${r.max_nights === 1 ? "" : "s"}.`
        : "Stay too long for the rules on this service.";
    case "min_lead_hours":
      return r.min_lead_hours
        ? `Bookings need to be made at least ${r.min_lead_hours} hour${r.min_lead_hours === 1 ? "" : "s"} in advance.`
        : "Too close to the booking time.";
    case "max_advance_days":
      return r.max_advance_days
        ? `Bookings can only be made up to ${r.max_advance_days} day${r.max_advance_days === 1 ? "" : "s"} ahead.`
        : "Too far in the future to book right now.";
    case "min_lead_minutes": {
      // availability_rules kind min_lead_minutes (20260913090030): exact notice
      // in minutes, checked by check_availability_rules on every write path.
      const m = typeof r.min_lead_minutes === "number" ? r.min_lead_minutes : null;
      if (!m) return "Too close to the booking time.";
      const t = m % 60 === 0 ? `${m / 60} hour${m === 60 ? "" : "s"}` : `${m} minutes`;
      return `Bookings need to be made at least ${t} in advance.`;
    }

    // ── Booking V3 attributed stock (check_resource_availability) ──
    // NEVER quote shortfall_units or pool_total_units — a guest is told free
    // or not, never a count. scope 'unit_required' is a unit-less write on a
    // service whose stock hangs off its options.
    case "resource_unavailable":
      return r.scope === "unit_required"
        ? "Choose one of the options to book this."
        : "That's no longer free for the time you picked — try another time, or fewer guests.";

    // ── Booking V3 option length / price tiers ──
    // A reschedule never re-prices: on a unit priced by length
    // (service_unit_price_tiers) a move to a length with a different price is
    // refused (20260913090050). The booking itself is unchanged.
    case "length_change_requires_rebook":
      return "This booking is priced by its length, so it can only move to another time of the same length. Cancel and book the new length instead.";

    // ── Staff-flow extras ──────────────────────────────────────────
    case "no_pending_bookings":
      return "Hold expired. Try again.";
    // No payment starts, and no staff pay link goes out, once a hold on the
    // cart has run out (20260927156010, 20260927156510). The widget and the
    // pay page say the same sentence.
    case "hold_expired":
      return "Your hold ended — please check availability again";
    case "insufficient_payment":
      return "This booking requires full payment before it can be confirmed.";

    // ── Event tickets (staff events screen) ────────────────────────
    case "event_capacity_exceeded":
      return "Not enough tickets left at this event.";
    case "ticket_type_sold_out":
      return "This ticket type is sold out.";
    case "event_sales_closed":
      return "Ticket sales for this event have closed.";
    case "event_sales_not_open":
      return "Ticket sales for this event haven't opened yet.";
    case "event_not_bookable":
      return "This event isn't open for booking.";
    // Event booking rules (event menus, 30 Sept). The fewest tickets one
    // booking of the event may have, counted across its ticket types when the
    // cart is paid or confirmed; `minimum` and `quantity` come with it.
    case "event_minimum_not_met": {
      const min = typeof r.minimum === "number" ? r.minimum : null;
      const qty = typeof r.quantity === "number" ? r.quantity : null;
      if (min === null) return "This event needs more guests per booking.";
      return qty === null
        ? `This event needs at least ${min} guests per booking.`
        : `This event needs at least ${min} guests per booking; this booking has ${qty}.`;
    }
    // A private sitting (events.one_booking_only) that another group holds or
    // has booked. Never says who.
    case "event_already_booked":
      return "This sitting has just been booked by another group.";
    // A sitting whose menu choice is one_per_booking (20260930170010): the
    // whole booking takes ONE of the offered menus, refused at the hold and
    // again at payment; `menu_id` is the menu the booking already has. The
    // always-offered menus (vegetarian, kids) are booked alongside.
    case "event_one_menu_per_booking":
      return "This sitting takes one menu for the whole group. Please choose one menu for everyone. The vegetarian and kids menus can still be added.";
    case "ticket_type_not_found":
      return "That ticket type is no longer available.";
    case "invalid_option_selection":
      return "One of the chosen add-ons is no longer available.";
    case "option_group_min":
    case "option_group_max":
      return "Check the add-on choices — some are required or over the limit.";
    case "no_contact":
      return "Add a phone or email to send the link to.";
    case "nothing_to_pay":
      return "These tickets are free — no payment link needed.";
    case "cooldown":
      return "A link was just sent — wait a moment before resending.";
    case "amount_too_large":
      return "That total is too large to process.";

    default:
      return `Couldn't complete the booking (${reason}).`;
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-ZA", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  } catch {
    return iso;
  }
}
